import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { Membership, Site } from "@sitebrew/contracts/v1";
import { sitesPage } from "@/lib/sites";
import { keys } from "@/lib/query-keys";
import { ButtonNav } from "@/components/button";
import { LoginCard, LoginCardSubtitle, LoginCardTitle } from "@/components/ui/login-card";
import { Badge } from "@/components/ui/badge";
import { ResourcePageLayout } from "@/components/resource-page";
import { DataTable, DataTableColumnHeader, DataTableSearch } from "@/components/data-table";
import { useResourceSearch } from "@/hooks";
import { useIsMobile } from "@/hooks/useIsMobile";
import { FormDialog } from "@/components/dialog";
import { NewSiteFields, useNewSiteForm } from "@/screens/new-site";

/**
 * What each status is called on screen, and how it is drawn.
 *
 * "Registered" and not "Live": M2 is the registry. Creating a site reserves its
 * name and makes its repository — nothing is served at the address yet, and a
 * console that said "live" would be describing a page that answers 404.
 *
 * The variant carries what the word alone does not: `creating` is a state that
 * moves on its own and `suspended` is one somebody has to do something about,
 * and a list of grey words says neither.
 */
const STATUS: Record<Site["status"], { label: string; variant: "secondary" | "outline" | "destructive" }> = {
  creating: { label: "Setting up", variant: "secondary" },
  ready: { label: "Registered", variant: "outline" },
  suspended: { label: "Suspended", variant: "destructive" },
};

/** One site's state, as the badge the fleet's lists use. */
export function SiteStatus({ status }: { status: Site["status"] }) {
  const { label, variant } = STATUS[status];

  return <Badge variant={variant}>{label}</Badge>;
}

/**
 * The sites of one workspace, as the fleet's table.
 *
 * It was a list of bordered links, which is what this screen could draw before
 * the fleet's layer was here. A `DataTable` is what the rest of the fleet's
 * consoles show a registry in, and it brings the three things the list did not
 * have: a search box, a pager, and headings that sort — so the columns are the
 * facts somebody scans, and the row is still the whole target it was.
 *
 * The data plane underneath is ours: `sitesPage` is a plain REST call to the
 * Worker, not the fleet's SQL-in-the-browser `DataSource` (design note §6).
 *
 * Four columns do not fit a phone — the first screenshot run drew "Name",
 * "Address" and "Status" one on top of the other and left a single character of
 * each — so on that viewport the table is Name and Status, and the address
 * moves under the name where the bordered list used to keep it. Nothing is
 * dropped; it is the same two facts in one column instead of two.
 *
 * `narrow` decides that here, rather than a `md:hidden` twin of the address
 * drawn always and hidden by CSS: the twin puts the same sentence in the
 * document twice, which is what anything reading the page — rather than looking
 * at it — then has to make sense of.
 */
function siteColumns(narrow: boolean): ColumnDef<Site>[] {
  return [
    {
      accessorKey: "name",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <span className="block min-w-0">
          <span className="block truncate font-bold">{row.original.name}</span>
          {narrow && (
            <span className="block truncate text-sm text-muted-foreground">
              {row.original.hostname ?? row.original.slug}
            </span>
          )}
        </span>
      ),
    },
    {
      // The address, which is the hostname once there is one and the slug that
      // stands in for it before that — the same fallback the row has always
      // shown, so sorting and searching agree with what is on screen.
      accessorKey: "hostname",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Address" />,
      cell: ({ row }) => (
        <span className="block truncate text-muted-foreground">
          {row.original.hostname ?? row.original.slug}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => <SiteStatus status={row.original.status} />,
      meta: { fixedWidth: true },
      size: 140,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
        </span>
      ),
      meta: { fixedWidth: true },
      size: 140,
    },
  ];
}

/**
 * The sites of one workspace, and the form that adds one.
 *
 * Creating is a dialog over this list rather than a page of its own — what is
 * being added is added to something, and the something stays visible. It is
 * still addressable (`/workspaces/$id/sites/new`), so the address bar and the
 * back button describe what is on screen: `creating` is the route saying so,
 * not a piece of state this screen keeps to itself.
 */
export function SitesScreen({
  workspace,
  creating,
}: {
  workspace: Membership;
  creating?: boolean;
}) {
  const navigate = useNavigate();
  const toList = () =>
    navigate({ to: "/workspaces/$workspaceId/sites", params: { workspaceId: workspace.id } });

  const form = useNewSiteForm(workspace, (site) =>
    navigate({ to: "/sites/$siteId", params: { siteId: site.id } }),
  );

  const { searchProps, listSearchProps } = useResourceSearch({ placeholder: "Search sites…" });
  // Which columns a phone has room for. The same breakpoint the sidebar uses,
  // from the same hook, so the layout changes in one step rather than the table
  // and the frame disagreeing about what a phone is.
  const isMobile = useIsMobile();
  const columns = useMemo(() => siteColumns(isMobile), [isMobile]);
  const columnVisibility = useMemo<Record<string, boolean>>(
    () => ({ hostname: !isMobile, createdAt: !isMobile }),
    [isMobile],
  );

  return (
    <ResourcePageLayout
      title="Sites"
      actionLabel="New site"
      headerActions={<DataTableSearch {...searchProps} />}
      onAction={() =>
        navigate({
          to: "/workspaces/$workspaceId/sites/new",
          params: { workspaceId: workspace.id },
        })
      }
    >
      <DataTable<Site>
        columns={columns}
        // The key of the *resource*, not of a page of it: `DataTable` appends
        // the page, the search and the sorting itself, so a create or an
        // archive invalidating `keys.sites(workspaceId)` still reaches every
        // page this table has cached.
        queryKey={keys.sites(workspace.id)}
        queryFn={(query) => sitesPage(workspace.id, query)}
        columnVisibility={columnVisibility}
        searchEnabled
        {...listSearchProps}
        onRowClick={(site) => navigate({ to: "/sites/$siteId", params: { siteId: site.id } })}
      />

      <FormDialog
        open={Boolean(creating)}
        onOpenChange={(open) => {
          if (!open) void toList();
        }}
        title="New site"
        description={`In ${workspace.name}.`}
        formState={form.form.formState}
        onReset={() => form.form.reset()}
      >
        <NewSiteFields {...form} />
      </FormDialog>
    </ResourcePageLayout>
  );
}

/**
 * A workspace this account is not a member of.
 *
 * The API answers `404` for "no such workspace" and for "not yours" alike, so
 * that somebody cannot learn which other workspaces exist by trying ids (§6).
 * The console is asked the same question here — the id comes from the URL — and
 * gives the same undivided answer rather than a friendlier one that leaks.
 */
export function UnknownWorkspaceScreen() {
  return (
    <LoginCard>
      <LoginCardTitle>No such workspace</LoginCardTitle>
      <LoginCardSubtitle>This account is not a member of a workspace with that address.</LoginCardSubtitle>
      <ButtonNav className="mt-[.9rem] w-full" variant="outline" to="/">
        ← Account
      </ButtonNav>
    </LoginCard>
  );
}
